import React from 'react';
import { Box, BoxProps } from '@mui/material';

/**
 * Official Flaha brand assets (source of truth: flaha.org/public/logos).
 * - App mark: logos/apps/flahafast.svg
 * - Divisions: flaha-main / flaha-ag / flaha-pa / flaha-la (PNG)
 */

export type LogoType =
  | 'flahafast' // App product mark (FlahaFAST)
  | 'flaha-agri' // Flaha Agri Tech
  | 'flaha-pa' // Precision Agriculture
  | 'flaha-main' // Main company
  | 'flaha-la'; // Landscape / LA division

export interface LogoProps extends Omit<BoxProps, 'component'> {
  type: LogoType;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  alt?: string;
  /** Load only when scrolled near. For logos below the fold. */
  lazy?: boolean;
}

const logoConfig: Record<
  LogoType,
  { src: string; alt: string; aspectRatio: string }
> = {
  flahafast: {
    src: '/assets/logos/flahafast-96.png',
    alt: 'FlahaFAST — hydroponic nutrient DSS',
    aspectRatio: '1 / 1',
  },
  'flaha-agri': {
    src: '/assets/logos/flaha-ag.png',
    alt: 'Flaha Agri Tech',
    aspectRatio: '1 / 1',
  },
  'flaha-pa': {
    src: '/assets/logos/flaha-pa.png',
    alt: 'Flaha PA — Precision Agriculture',
    aspectRatio: '1 / 1',
  },
  'flaha-main': {
    src: '/assets/logos/flaha-main.png',
    alt: 'Flaha',
    aspectRatio: '1 / 1',
  },
  'flaha-la': {
    src: '/assets/logos/flaha-la.png',
    alt: 'Flaha LA',
    aspectRatio: '1 / 1',
  },
};

const sizeConfig = {
  small: { height: 32, width: 32 },
  medium: { height: 48, width: 48 },
  large: { height: 64, width: 64 },
  xlarge: { height: 96, width: 96 },
};

const Logo: React.FC<LogoProps> = ({
  type,
  size = 'medium',
  alt,
  lazy,
  sx,
  ...boxProps
}) => {
  const config = logoConfig[type];
  const sizeStyle = sizeConfig[size];

  return (
    <Box
      component="img"
      src={config.src}
      alt={alt || config.alt}
      sx={{
        ...sizeStyle,
        aspectRatio: config.aspectRatio,
        objectFit: 'contain',
        display: 'block',
        flexShrink: 0,
        ...sx,
      }}
      loading={lazy ? 'lazy' : undefined}
      decoding={lazy ? 'async' : undefined}
      {...boxProps}
    />
  );
};

export default Logo;
